/**
 * Domain types shared by the UI, the mock repositories and (later) the
 * Supabase repositories. Field names mirror the production table design in
 * section 19 of the design document so the prototype records can move to
 * Postgres with minimal translation.
 *
 * All identifiers are UUID strings. All timestamps are ISO-8601 strings.
 */

export type UUID = string;
export type ISODateTime = string;
export type Lang = 'en' | 'he';

// ---------------------------------------------------------------------------
// Dates with uncertainty (section 11.3 / 19.2)
// ---------------------------------------------------------------------------

export type DateKind =
  | 'exact'
  | 'month'
  | 'year'
  | 'decade'
  | 'range'
  | 'about'
  | 'relative'
  | 'before_after'
  | 'unknown';

export type DateCertainty = 'confirmed' | 'approximate' | 'uncertain';

export interface FuzzyDate {
  kind: DateKind;
  /** ISO date (YYYY-MM-DD) or partial (YYYY-MM, YYYY). Start of the range for `range`/`decade`. */
  start?: string;
  /** ISO date; end of the range for `range`/`decade`. */
  end?: string;
  /** The human-entered expression, e.g. "I was about 12", "before the wedding". */
  displayText?: string;
  certainty?: DateCertainty;
  /** For `relative`: age of a reference person at the time. */
  relativePersonId?: UUID;
  relativeAge?: number;
  /** For `before_after`: the event or story this date is relative to. */
  relativeEventId?: UUID;
  beforeAfter?: 'before' | 'after';
}

// ---------------------------------------------------------------------------
// People (section 5.1, 11)
// ---------------------------------------------------------------------------

export type LivingStatus = 'living' | 'deceased' | 'unknown';
export type Gender = 'female' | 'male' | 'other' | 'unspecified';
export type ReviewStatus = 'approved' | 'pending' | 'rejected' | 'disputed';

export interface PersonName {
  id: UUID;
  personId: UUID;
  language: Lang;
  given: string;
  middle?: string;
  family: string;
  nickname?: string;
  /** Optional explicit display form; otherwise given + family. */
  display?: string;
  preferred: boolean;
  previousNames?: string[];
}

export interface VersionedRecord {
  version: number;
  createdAt: ISODateTime;
  updatedAt: ISODateTime;
  updatedBy: UUID | null;
}

export interface Person extends VersionedRecord {
  id: UUID;
  familyId: UUID;
  status: LivingStatus;
  gender: Gender;
  birth: FuzzyDate;
  death?: FuzzyDate;
  birthPlaceId?: UUID;
  /**
   * Where this person lives now (city level, see `Location`). Changing it
   * appends a `PersonPlace` row so the app remembers every home since the
   * person joined.
   */
  homeLocationId?: UUID;
  /** Data URL or storage key; undefined → initials are shown. */
  portraitUrl?: string;
  /** Deterministic accent colour token index for initials avatars. */
  portraitHue: number;
  reviewStatus: ReviewStatus;
  /** Field names an administrator locked against ordinary changes. */
  lockedFields: string[];
  lockReason?: string;
  /**
   * Whole-record lock set by an administrator (for someone who has passed
   * away, or who cannot join yet). Anyone may still open the edit form, but
   * their changes become suggestions that an administrator approves.
   */
  locked?: boolean;
  archived: boolean;
  /** Set when this record was merged into another one; references survive. */
  mergedIntoId?: UUID;
  notes?: string;
  /** How the family may reach this person (shown on their circle only while `shared`). */
  contact?: PersonContact;
}

/**
 * A phone number and/or email the person chose to share with the family, so
 * a circle in the tree can become a call or a WhatsApp message. Follows the
 * same edit rules as every other personal detail (`personEditMode`). Nothing
 * is shown to anyone unless `shared` is true.
 */
export interface PersonContact {
  phone?: string;
  email?: string;
  /**
   * Where else the family can find them. Stored exactly as it was typed — a
   * handle, an @handle or a pasted address — and turned into a link by
   * `lib/social`, so nothing is lost if the shape of those links ever changes.
   * Absent means no button: a circle never advertises an account nobody gave it.
   */
  instagram?: string;
  facebook?: string;
  shared: boolean;
}

export interface PersonWithNames extends Person {
  names: PersonName[];
}

/**
 * The big family moments — the simchas and the milestones — as opposed to an
 * everyday get-together. A milestone is always an event (`isEvent`), with
 * `milestone` saying which kind; `other` carries its own title.
 */
export const MILESTONE_KINDS = ['wedding', 'engagement', 'birth', 'brit', 'barMitzvah', 'batMitzvah', 'graduation', 'newHome', 'aliyah', 'anniversary', 'memorial', 'other'] as const;
export type MilestoneKind = (typeof MILESTONE_KINDS)[number];

/**
 * The things on the family's timeline that can hold pictures.
 *
 * Every line on it is a day the family remembers, and every one of them takes
 * photos and clips: a simcha or an everyday event (`moment`), a story someone
 * recorded (`story`), and the day a person was born (`birth`). The pictures are
 * ordinary Moments that name what they were added to, so one picture is
 * findable three ways — inside the thing, in the Moments tab, and under the
 * circle of whoever uploaded it.
 *
 * A **recipe** takes pictures the same way and is the one kind here that is not
 * a day: it has no date and no constellation of its own, and it is never on the
 * timeline or the calendar. It is an anchor all the same because the thing the
 * family does to it is exactly what they do to a wedding — "here is a picture of
 * it" — and a picture added that way should get everything a picture anywhere
 * else in the app already has: it opens large, it takes a heart, an
 * administrator can take it down and put it back. One of them stands for the
 * recipe (`Recipe.coverPhotoId`).
 */
export type MemoryAnchorKind = 'moment' | 'story' | 'birth' | 'recipe';

export interface MemoryAnchor {
  kind: MemoryAnchorKind;
  /** The Moment, the Story, the person whose birth this is, or the Recipe. */
  id: UUID;
}

/**
 * What a constellation is drawn around: one of the days above, or a person.
 *
 * A person is deliberately *not* a fourth `MemoryAnchor` kind, because nothing
 * is ever added *to* a person the way a photograph is added to a wedding: a
 * picture of somebody is a moment of its own with their face tagged on it, and
 * it belongs to them by that tag rather than by hanging off them. So a day's
 * constellation is everything `addedTo` it, and a person's constellation is
 * everything that names them — their pictures, their portraits and the stories
 * they are in, newest nearest.
 */
export type MemorySubject = MemoryAnchor | { kind: 'person'; id: UUID };

/**
 * A picture shared with the whole family from the Photos tab — a snapshot, a
 * gathering, a document. Marked `isEvent` when it records an occasion: an
 * everyday get-together (a dinner, a trip), or — with `milestone` set — one
 * of the family's big moments (a wedding, a bar mitzvah, a birth), which the
 * app treats as more important. Later iterations may surface upcoming events.
 *
 * A Moment is also how the family fills up a day it remembers: everything
 * added to a simcha, a story or a birth is one of these, carrying `addedTo`
 * and hanging off that one day rather than becoming a day of its own.
 */
export interface FamilyPhoto {
  id: UUID;
  familyId: UUID;
  uploadedByAccountId: UUID;
  /** Absent for an event added without a picture. */
  storageKey?: string;
  /**
  * `image/…` for a picture, `video/…` for a clip, `audio/…` for a voice note
  * somebody recorded about the day. All three live in the same store and are
  * the same kind of record: something a relative added to a day the family
  * remembers.
  */
  mimeType?: string;
  bytes?: number;
  width?: number;
  height?: number;
  /** How long a clip or a voice note runs. */
  durationMs?: number;
  /** Event name ("Noa's bat mitzvah"); events without a picture must have one. */
  title?: string;
  caption?: string;
  isEvent: boolean;
  /** Set when the event is one of the family's milestones (a simcha); absent for everyday events. */
  milestone?: MilestoneKind;
  /**
   * What the family added this picture or clip to, if anything: a milestone or
   * event, a story, or someone's birth. Set on everything contributed
   * afterwards, so a wedding is one line on the timeline with fifty pictures
   * inside it rather than fifty weddings, and a record that is part of
   * something else is never a day of its own.
   */
  addedTo?: MemoryAnchor;
  /** When the pictured moment happened (not when it was uploaded). */
  takenOn?: FuzzyDate;
  /** People in the picture, when tagged. */
  personIds: UUID[];
  /**
   * Chosen to stand for the day, the story or the birth it was added to — on
   * the family's line, and in the middle of that thing's constellation. At most
   * one picture of a thing carries it; with none, the first picture added is the
   * main one (`coverOf` in domain/covers.ts, where the whole rule is). A recipe
   * keeps its choice on the recipe instead (`Recipe.coverPhotoId`).
   */
  isCover?: boolean;
  /** One emoji per account — a light, wordless response. */
  reactions?: PhotoReaction[];
  createdAt: ISODateTime;
  /** Taken down by an administrator; the picture itself is kept (see MediaRemoval). */
  removed?: MediaRemoval;
}

export interface Reaction {
  accountId: UUID;
  emoji: string;
  createdAt: ISODateTime;
}
/** @deprecated use Reaction — kept so older imports still compile. */
export type PhotoReaction = Reaction;

/** A person this account chose to follow closely; their circle is highlighted in the tree. */
export interface PersonStar {
  id: UUID;
  accountId: UUID;
  personId: UUID;
  createdAt: ISODateTime;
}

/**
 * How the tree breathes (admin-tunable): a circle grows when something new
 * happens to the person and eases back to its default size over `nodeFadeDays`;
 * the badges on its rim are born at `badgeMaxPx` and shrink to `badgeMinPx`
 * over `badgeFadeDays`.
 */
export interface TreeSettings {
  familyId: UUID;
  nodeScaleDefault: number;
  nodeScaleMax: number;
  nodeFadeDays: number;
  badgeMinPx: number;
  badgeMaxPx: number;
  badgeFadeDays: number;
  /** Globe: how big a circle is drawn at world zoom (× its 46px) … */
  globeZoomMin: number;
  /** … and at the closest zoom; it grows steadily between the two as the globe zooms in. */
  globeZoomMax: number;
  updatedAt: ISODateTime;
}

export const DEFAULT_TREE_SETTINGS: Omit<TreeSettings, 'familyId' | 'updatedAt'> = {
  nodeScaleDefault: 1,
  nodeScaleMax: 1.35,
  nodeFadeDays: 14,
  badgeMinPx: 16,
  badgeMaxPx: 30,
  badgeFadeDays: 14,
  globeZoomMin: 0.7,
  globeZoomMax: 1.8,
};

/**
 * A photo attached to a person's circle in the tree. Every photo ever added is
 * kept (nothing is overwritten or deleted by ordinary users); exactly one is
 * `current` and shown in the circle. `Person.portraitUrl` holds
 * `portrait:<id>` pointing at the current one.
 */
export interface PersonPortrait {
  id: UUID;
  familyId: UUID;
  personId: UUID;
  /** Binary storage key (blob store in the prototype; Storage bucket path in production). */
  storageKey: string;
  mimeType: string;
  bytes: number;
  width: number;
  height: number;
  uploadedByAccountId: UUID;
  createdAt: ISODateTime;
  current: boolean;
  /**
   * Taken down by an administrator. The row and the image itself are kept, so
   * the admin decision log can still show what was removed and why.
   */
  removed?: MediaRemoval;
}

/**
 * An administrator took a picture down. Nothing is deleted: the image stays in
 * storage and this record says who removed it, when, and — if they chose to
 * say — why. Admin controls → Admin decisions shows both.
 */
export interface MediaRemoval {
  byAccountId: UUID;
  at: ISODateTime;
  reason?: string;
}

// ---------------------------------------------------------------------------
// Relationships (section 5.3, 10, 19.1)
// ---------------------------------------------------------------------------

export type RelationshipType = 'parent_child' | 'partner';
export type ParentSubtype = 'biological' | 'adoptive' | 'step' | 'guardian' | 'foster' | 'unspecified';
export type PartnerSubtype = 'married' | 'partnered' | 'unspecified';

export interface Relationship extends VersionedRecord {
  id: UUID;
  familyId: UUID;
  type: RelationshipType;
  /** parent_child: fromPersonId is the parent, toPersonId is the child. partner: canonical order (lower id first). */
  fromPersonId: UUID;
  toPersonId: UUID;
  subtype: ParentSubtype | PartnerSubtype;
  /** partner only */
  current?: boolean;
  startDate?: FuzzyDate;
  endDate?: FuzzyDate;
  status: ReviewStatus;
  note?: string;
  locked: boolean;
}

// ---------------------------------------------------------------------------
// Accounts, memberships, access (section 7, 8)
// ---------------------------------------------------------------------------

export type Role = 'member' | 'helper' | 'editor' | 'admin';
export type AccountKind = 'email' | 'phone' | 'temporary';

export interface Account {
  id: UUID;
  kind: AccountKind;
  email?: string;
  phone?: string;
  displayName: string;
  preferredLang: Lang;
  createdAt: ISODateTime;
  /** Temporary accounts have no recovery identity until linked. */
  hasRecoveryIdentity: boolean;
}

export interface FamilyMembership {
  id: UUID;
  familyId: UUID;
  accountId: UUID;
  role: Role;
  status: 'active' | 'suspended' | 'removed';
  /**
   * When this member last closed the family's welcome message (domain/welcome.ts).
   * Absent until they have closed it once. Kept with the account rather than on
   * the device, so that somebody who reads it on their phone is not read it again
   * by their computer — and compared with `Family.welcome.updatedAt`, which is
   * how a message an administrator has changed comes up again for everyone.
   */
  welcomeSeenAt?: ISODateTime;
  createdAt: ISODateTime;
}

export interface PersonAccountLink {
  id: UUID;
  accountId: UUID;
  personId: UUID;
  kind: 'self';
  status: 'active' | 'pending' | 'revoked';
  createdAt: ISODateTime;
}

export interface ProfileManagementGrant {
  id: UUID;
  familyId: UUID;
  helperAccountId: UUID;
  managedPersonId: UUID;
  scope: 'full' | 'record_only';
  grantedByAccountId: UUID;
  status: 'active' | 'revoked' | 'expired';
  expiresAt?: ISODateTime;
  createdAt: ISODateTime;
}

export interface AccessCode {
  id: UUID;
  familyId: UUID;
  /** Prototype only: production stores a hash. */
  code: string;
  label: string;
  expiresAt: ISODateTime;
  revoked: boolean;
  maxUses: number;
  uses: number;
  createdAt: ISODateTime;
}

export interface Invitation {
  id: UUID;
  familyId: UUID;
  /** Prototype only: production stores a hash. */
  token: string;
  intendedPersonId: UUID;
  destination: string;
  expiresAt: ISODateTime;
  status: 'pending' | 'accepted' | 'expired' | 'revoked';
  createdByAccountId: UUID;
  createdAt: ISODateTime;
}

export interface TemporaryAccessSession {
  id: UUID;
  familyId: UUID;
  personId: UUID;
  accountId: UUID;
  /** Prototype only: production stores a hash. */
  code: string;
  deviceLabel: string;
  issuedByAccountId: UUID;
  issuedAt: ISODateTime;
  expiresAt: ISODateTime;
  revoked: boolean;
  redeemedAt?: ISODateTime;
  pinHash?: string;
}

export interface Family {
  id: UUID;
  name: string;
  nameHe: string;
  defaultLang: Lang;
  settings: {
    memberRelationshipChanges: 'review';
    allowUnknownBirthDate: boolean;
  };
  governance: Governance;
  /**
   * Who answers the Help button, set once by an administrator for everybody.
   *
   * It lives on the family rather than in each browser because that is the only
   * way it can be set once and be true for the grandmother who will never open
   * Admin controls. It also means it survives anything that clears a browser's
   * local storage — which is how the first version of this lost a key that had
   * been carefully typed in the day before.
   */
  assistant?: AssistantConfig;
  /**
   * The welcome message, once an administrator has written one. Until then the
   * app's own words are shown (`greeting.default`) — see domain/welcome.ts.
   */
  welcome?: WelcomeMessage;
  createdAt: ISODateTime;
}

/**
 * What the family reads on the billboard the first time they open the app, and
 * again whenever it changes.
 *
 * In both of the family's languages, either of which may be left empty: a reader
 * gets their own when it was written and the other when it was not. Plain words
 * and blank lines between paragraphs — nothing else. `updatedAt` is the whole of
 * the "has it changed?" mechanism: it is compared with each member's
 * `welcomeSeenAt`.
 */
export interface WelcomeMessage {
  en: string;
  he: string;
  updatedAt: ISODateTime;
  updatedBy: UUID;
}

/**
 * The family's assistant, as the administrators set it.
 *
 * **A word about the key.** Putting it here means every signed-in member's
 * browser can read it, because every member's browser has to be able to make the
 * call. That is the honest cost of "set it once for everyone" in an app with no
 * server, and it is the same trust boundary the rest of this prototype already
 * has — the family's photographs and recordings are equally readable to anyone
 * holding a signed-in device. It is still not what you want once real money is
 * attached to the key, which is exactly what `endpoint` is for: point the family
 * at an address you run, and the key never leaves your server. The Admin
 * controls screen says all of this next to the field.
 *
 * Personal preferences — whether *you* want to speak to it, whether *you* want
 * it read aloud — are not here. Those stay on the device, because they are
 * nobody else's business.
 */
export interface AssistantConfig {
  /** 'local' | 'anthropic' | 'openai' | 'proxy'. */
  provider: string;
  model: string;
  apiKey: string;
  baseUrl: string;
  /**
   * Listening and speaking are two services, so they are two settings.
   * `listenWith`: 'off' | 'browser' | 'openai' | 'proxy'.
   * `speakWith`: the same, plus 'speechify', which does speaking only.
   */
  listenWith: string;
  listenKey: string;
  listenUrl: string;
  speakWith: string;
  speakKey: string;
  speakUrl: string;
  /** Which voice, when the speaking service offers a choice. */
  voiceName: string;
  /**
   * Choices for the app's own server, which holds the keys: how long the model
   * thinks (`quick` | `balanced` | `careful`), and which listening and voice
   * models it uses. Empty or absent means the server's default.
   */
  effort?: string;
  listenModel?: string;
  speakModel?: string;
  updatedAt?: ISODateTime;
  updatedBy?: UUID;
}

/**
 * Who runs the family, and how that changes.
 *
 * Administrators are named by **email address**: an account whose email is on
 * this list is an administrator of the family, whichever device or sign-in
 * method they use. The list itself is only ever changed by agreement —
 * `AdminNomination` below — never by one person acting alone.
 *
 * The same section holds the one other number the family votes with: how many
 * relatives have to recognise a newly added person before their circle stops
 * being dimmed.
 */
export interface Governance {
  /** Lower-cased email addresses. Every account signing in with one of these is an administrator. */
  adminEmails: string[];
  /**
   * An administrator counts towards "a majority of administrators" only if they
   * have done something in the family within this many days. Admins who have
   * drifted away cannot silently block every decision.
   */
  activeAdminDays: number;
  /**
   * The safety valve for when the majority never arrives: an open nomination
   * with at least one approval and no objections passes on its own after this
   * many days.
   */
  nominationTimeoutDays: number;
  /** How many relatives must recognise a new person before their circle is no longer dimmed. */
  memberApprovalsRequired: number;
}

export const DEFAULT_GOVERNANCE: Governance = {
  adminEmails: [],
  activeAdminDays: 30,
  nominationTimeoutDays: 14,
  memberApprovalsRequired: 2,
};

export type AdminNominationStatus = 'open' | 'approved' | 'declined' | 'withdrawn';

/** One administrator's answer to a nomination. Silence is neither. */
export interface AdminVote {
  accountId: UUID;
  vote: 'approve' | 'object';
  reason?: string;
  at: ISODateTime;
}

/**
 * A proposal to make someone an administrator. It passes when a majority of the
 * *active* administrators approve, or — so that an inactive board cannot freeze
 * the family — when it has stood for `nominationTimeoutDays` with at least one
 * approval and nobody objecting. A single objection takes the timeout away, so
 * a contested nomination always needs the real majority.
 */
export interface AdminNomination {
  id: UUID;
  familyId: UUID;
  /** Lower-cased. The person need not have an account yet; the role attaches when they sign in. */
  email: string;
  /** Set when an account with that email already exists (for the avatar and the name). */
  accountId?: UUID;
  nominatedByAccountId: UUID;
  reason?: string;
  votes: AdminVote[];
  status: AdminNominationStatus;
  /** How it ended, once it has: enough approvals, or nobody objected in time. */
  decidedBy?: 'majority' | 'timeout' | 'objection' | 'withdrawn';
  createdAt: ISODateTime;
  decidedAt?: ISODateTime;
}

/**
 * A proposal to take a circle out of the tree.
 *
 * Removing someone the family can see is never one administrator's decision:
 * it goes to the same agreement as adding an administrator (see
 * domain/consensus.ts). When it carries the circle is archived, not destroyed —
 * the record, its stories and the reason all stay, and it can be restored.
 */
export type PersonRemovalStatus = 'open' | 'approved' | 'declined' | 'withdrawn';

export interface PersonRemovalProposal {
  id: UUID;
  familyId: UUID;
  personId: UUID;
  proposedByAccountId: UUID;
  /** Why this circle should go. Asked for, because removal is not obvious later. */
  reason?: string;
  votes: AdminVote[];
  status: PersonRemovalStatus;
  decidedBy?: 'majority' | 'timeout' | 'objection' | 'withdrawn';
  createdAt: ISODateTime;
  decidedAt?: ISODateTime;
}

/**
 * One relative saying "yes, I know this person" about a newly added circle.
 * Enough of these (see `Governance.memberApprovalsRequired`) — or a single
 * administrator — turn a dimmed circle into a full member of the tree.
 */
export interface PersonApproval {
  id: UUID;
  familyId: UUID;
  personId: UUID;
  accountId: UUID;
  /** True when an administrator approved outright rather than vouching as one of the family. */
  byAdmin: boolean;
  createdAt: ISODateTime;
}

// ---------------------------------------------------------------------------
// Stories, contributions, events, media (section 5.4, 12, 13)
// ---------------------------------------------------------------------------

export type StoryStatus = 'draft' | 'published' | 'archived';

/*
 * A published story belongs to the whole family. There is no audience to
 * choose and no way to narrow one: the app exists so that what a grandmother
 * says is heard by everyone who comes after her, and a story nobody can find
 * is a story that was not kept. (A draft is still only the recorder's, until
 * they publish it.)
 */
export interface Story extends VersionedRecord {
  id: UUID;
  familyId: UUID;
  title: string;
  titlePending: boolean; // "Help me title it later"
  summary?: string;
  date: FuzzyDate;
  locationId?: UUID;
  status: StoryStatus;
  creatorAccountId: UUID;
  /** The person whose story this primarily is (primary storyteller). */
  primaryStorytellerPersonId: UUID;
  publishedAt?: ISODateTime;
  locked: boolean;
  /** Idempotency key of the command that created this story. */
  idempotencyKey: string;
  /** One emoji per account — the same wordless response Moments have; tells the teller they were heard. */
  reactions?: Reaction[];
}

/**
 * A change somebody who did not record the story would like made to it.
 *
 * A story belongs to whoever recorded it. Anyone else who spots a wrong year
 * or a missing cousin does not edit it behind their back — they say what they
 * think it should be, and the person whose story it is says yes or no. (An
 * administrator changes it directly; that is what the purple is for.)
 *
 * Three kinds, matching the three things there are to get wrong: what it is
 * called, when it happened, and who is in it.
 */
export type StorySuggestionChange =
  | { kind: 'title'; title: string }
  | { kind: 'date'; date: FuzzyDate }
  | { kind: 'people'; personIds: UUID[] };

export type StorySuggestionKind = StorySuggestionChange['kind'];
export type StorySuggestionStatus = 'open' | 'accepted' | 'declined' | 'withdrawn';

export interface StorySuggestion {
  id: UUID;
  familyId: UUID;
  storyId: UUID;
  change: StorySuggestionChange;
  /** Why they think so, in their own words. Optional, and never invented. */
  note?: string;
  suggestedByAccountId: UUID;
  status: StorySuggestionStatus;
  createdAt: ISODateTime;
  decidedAt?: ISODateTime;
  decidedByAccountId?: UUID;
}

export type StoryPersonRole = 'storyteller' | 'subject' | 'participant' | 'mentioned';

export interface StoryPerson {
  id: UUID;
  storyId: UUID;
  personId: UUID;
  role: StoryPersonRole;
}

export type ContributionKind = 'audio' | 'text';
export type ContributionStatus = 'draft' | 'published' | 'removed';

export interface StoryContribution extends VersionedRecord {
  id: UUID;
  storyId: UUID;
  speakerPersonId: UUID;
  uploaderAccountId: UUID;
  order: number;
  kind: ContributionKind;
  mediaAssetId?: UUID;
  text?: string;
  /** A contributor may offer their own reading of when it happened. */
  dateInterpretation?: FuzzyDate;
  status: ContributionStatus;
  idempotencyKey: string;
}

export interface FamilyEvent extends VersionedRecord {
  id: UUID;
  familyId: UUID;
  title: string;
  titleHe?: string;
  description?: string;
  date: FuzzyDate;
  locationId?: UUID;
  status: 'confirmed' | 'approximate' | 'proposed';
  locked: boolean;
}

export interface StoryEvent {
  id: UUID;
  storyId: UUID;
  eventId: UUID;
}

/**
 * "Ask them something": a question one member asks a person, answered with a
 * story. The question waits in the recipient's bell and on their profile
 * until someone who records for that person answers it (the answer is an
 * ordinary story, linked back here) or the person waves it away.
 */
export type StoryQuestionStatus = 'open' | 'answered' | 'dismissed';

export interface StoryQuestion {
  id: UUID;
  familyId: UUID;
  toPersonId: UUID;
  askedByAccountId: UUID;
  /** The asker's own circle, when they have one (for the avatar). */
  askedByPersonId?: UUID;
  text: string;
  status: StoryQuestionStatus;
  answerStoryId?: UUID;
  createdAt: ISODateTime;
  answeredAt?: ISODateTime;
}

// ---------------------------------------------------------------------------
// Recipes — the family's kitchen, kept under the circle of whoever cooks it
// ---------------------------------------------------------------------------

/**
 * A recipe as it is read: a name, a line about the dish, a few plain facts, what
 * goes in, what you do, and whatever else is worth knowing.
 *
 * Deliberately *lines of words* and nothing cleverer. There is no quantity
 * field, no unit table, no timer — a grandmother says "a glass of oil" and "until
 * it looks right", and a schema that cannot hold that is a schema that loses the
 * recipe. Every line is a sentence somebody said, tidied; which is also what lets
 * one line be pointed at ("line 5 is wrong") and one line be corrected, by voice
 * or by hand, without the rest moving. See `domain/recipes.ts` for how the lines
 * are numbered.
 *
 * One convention, borrowed from how people already write recipes down: an
 * ingredient or a step that ends with a colon — "For the syrup:" — is a heading
 * for the lines under it rather than a line of its own.
 */
export interface RecipeDoc {
  title: string;
  /** A sentence or two about the dish: whose it is, when it is made, what it means. Empty when nobody said. */
  intro: string;
  /** Short standalone facts, only if they were given: "Serves 8", "About 2 hours", "Parve". */
  facts: string[];
  ingredients: string[];
  steps: string[];
  /** Tips, variations, how it keeps, who taught it. */
  notes: string[];
}

/** A draft is only its writer's, exactly like a story; archived is "taken down", never destroyed. */
export type RecipeStatus = 'draft' | 'published' | 'archived';

/**
 * One recipe the family keeps.
 *
 * It hangs on a **person's circle** and nowhere else — not on the timeline, not
 * on the calendar. A recipe is not a day that happened; it is something a person
 * makes, and the place anyone looks for Savta's fish is Savta. So it carries two
 * people, for the same reason a story does: `personId` is whose recipe it *is*,
 * and `creatorAccountId` is whoever sat down and wrote it out — very often a
 * grandchild, for somebody who never will. It shows on both their circles.
 */
export interface Recipe extends VersionedRecord {
  id: UUID;
  familyId: UUID;
  /** Whose recipe it is — the circle it hangs on. */
  personId: UUID;
  /** Who wrote it down. It is theirs to change. */
  creatorAccountId: UUID;
  /** The language it was told in. A recipe is never machine-translated, like a name. */
  lang: Lang;
  status: RecipeStatus;
  /** What the family reads. While `status` is `draft` this is the working copy itself. */
  doc: RecipeDoc;
  /**
   * Changes to a published recipe that have not been published yet.
   *
   * The back-and-forth with the assistant can take a while and can go wrong, and
   * none of that should be happening live on a page the family may be cooking
   * from. So once a recipe is out, editing works on this copy and **Publish
   * changes** swaps it in. Only whoever may edit the recipe ever receives it.
   */
  draft?: RecipeDoc;
  /**
   * The words it was first told in, exactly as they were heard or typed. Kept so
   * that a write-up that fails, or comes out wrong, never costs anybody the
   * telling — it can always be written up again.
   */
  source?: string;
  /**
   * The picture the family sees first: on the Passed Down shelf, and across the
   * top of the page.
   *
   * The pictures themselves are not kept here. Anybody in the family can add one
   * to a recipe — the cousin who finally made Savta's fish, as much as whoever
   * wrote it down — and each is an ordinary Moment naming this recipe through
   * `addedTo`. This only says which of them stands for it. It is empty most of
   * the time, and that is deliberate: until somebody chooses, the first picture
   * anyone added is the one (`recipeCover` in domain/recipes), so a recipe gets
   * a face the moment it has a picture and nobody ever has to "set" anything.
   */
  coverPhotoId?: UUID;
  publishedAt?: ISODateTime;
  /** One emoji per account — the same wordless response a story takes. */
  reactions?: Reaction[];
  /** Idempotency key of the command that created it. */
  idempotencyKey: string;
}

/**
 * How precisely a location is known. The family sees a city on the globe;
 * nothing finer unless the person chose to add it.
 */
export type LocationPrecision = 'city' | 'area' | 'exact';

export interface Location {
  id: UUID;
  label: string;
  labelHe?: string;
  historicalName?: string;
  lat?: number;
  lng?: number;
  /** IANA time zone, e.g. "America/Toronto" — lets the app know local time / sunset there. */
  timezone?: string;
  precision?: LocationPrecision;
  /** ISO 3166-1 alpha-2, when known. */
  countryCode?: string;
  /** Stable key of the bundled city this was picked from (`src/data/cities`), so two people picking "Haifa" share one row. */
  cityRef?: string;
}

/**
 * One stretch of time a person spent somewhere — their home (`kind: 'home'`,
 * open-ended while current) or a visit with known dates. Rows are never
 * deleted: a called-off visit is marked `cancelled`, a previous home is
 * closed by setting `to`. Together they are the person's travel record since
 * the app began.
 */
export type PersonPlaceKind = 'home' | 'visit';

export interface PersonPlace {
  id: UUID;
  familyId: UUID;
  personId: UUID;
  locationId: UUID;
  kind: PersonPlaceKind;
  /** First day, YYYY-MM-DD (local date where the person is). */
  from: string;
  /** Last day, YYYY-MM-DD inclusive; absent = still current. */
  to?: string;
  note?: string;
  cancelled?: boolean;
  createdByAccountId: UUID;
  createdAt: ISODateTime;
}

export type MediaStatus =
  | 'local' // saved on this device only
  | 'uploading'
  | 'paused_retry' // upload paused, will retry automatically
  | 'action_needed' // retries exhausted; user or admin action required
  | 'uploaded' // server confirmed complete original + metadata committed
  | 'backed_up' // independent backup confirmed as well
  | 'failed_permanent';

export interface MediaAsset {
  id: UUID;
  familyId: UUID;
  kind: 'audio' | 'image' | 'document';
  role: 'original' | 'derived';
  mimeType: string;
  durationMs?: number;
  sizeBytes: number;
  /** IndexedDB blob key (prototype) or storage object path (production). */
  storageKey: string;
  checksum?: string;
  language?: Lang | 'unknown';
  speakerPersonId?: UUID;
  uploaderAccountId: UUID;
  status: MediaStatus;
  backupState: 'none' | 'pending' | 'done' | 'failed';
  createdAt: ISODateTime;
  updatedAt: ISODateTime;
  /** Placeholder audio generated for demo seed data. */
  isPlaceholder?: boolean;
}

export interface UploadSession {
  id: UUID;
  mediaAssetId: UUID;
  localDraftId: UUID;
  idempotencyKey: string;
  totalBytes: number;
  bytesUploaded: number;
  state: 'idle' | 'uploading' | 'paused' | 'verifying' | 'committing' | 'backing_up' | 'complete' | 'action_needed';
  attempts: number;
  lastError?: string;
  nextRetryAt?: ISODateTime;
  createdAt: ISODateTime;
  updatedAt: ISODateTime;
}

export type TranscriptStatus = 'pending' | 'processing' | 'ready' | 'failed' | 'unavailable';

export interface Transcript {
  id: UUID;
  contributionId: UUID;
  language: Lang | 'unknown';
  text: string;
  isTranslation: boolean;
  source: 'ai' | 'human';
  status: TranscriptStatus;
  reviewStatus: 'unreviewed' | 'approved' | 'corrected';
  version: number;
  updatedAt: ISODateTime;
  error?: string;
}

// ---------------------------------------------------------------------------
// Review, audit, notifications (section 10.5, 16.6, 18)
// ---------------------------------------------------------------------------

export type ChangeRequestKind =
  | 'relationship_add'
  | 'relationship_remove'
  | 'relationship_change'
  | 'person_field'
  | 'person_add'
  | 'claim'
  | 'merge'
  | 'conflict_escalation';

export type ChangeRequestStatus = 'pending' | 'approved' | 'rejected' | 'question';

export interface ChangeRequest {
  id: UUID;
  familyId: UUID;
  kind: ChangeRequestKind;
  /** Kind-specific payload, kept JSON-serialisable. */
  payload: Record<string, unknown>;
  summary: string;
  summaryHe?: string;
  requesterAccountId: UUID;
  onBehalfOfPersonId?: UUID;
  reason?: string;
  status: ChangeRequestStatus;
  reviewerAccountId?: UUID;
  reviewerQuestion?: string;
  resolution?: string;
  targetEntityType?: 'person' | 'relationship' | 'story';
  targetEntityId?: UUID;
  createdAt: ISODateTime;
  updatedAt: ISODateTime;
}

export interface DuplicateSuggestion {
  id: UUID;
  familyId: UUID;
  personAId: UUID;
  personBId: UUID;
  confidence: number; // 0..1
  reason: string;
  status: 'open' | 'merged' | 'dismissed';
  createdAt: ISODateTime;
}

export type AuditAction =
  | 'create'
  | 'update'
  | 'archive'
  | 'restore'
  | 'approve'
  | 'reject'
  | 'merge'
  | 'lock'
  | 'unlock'
  | 'publish'
  | 'remove'
  | 'role_change'
  | 'grant'
  | 'revoke'
  | 'sign_in'
  | 'claim';

/**
 * A picture an entry in the history refers to — the photo an administrator took
 * down, kept so the decision can be seen rather than only read about.
 * `src` is what the app resolves to an image (`portrait:<id>` / `moment:<id>`).
 */
export interface AuditMedia {
  kind: 'portrait' | 'moment';
  id: UUID;
  src: string;
}

export interface AuditEvent {
  id: UUID;
  familyId: UUID;
  actorAccountId: UUID | null;
  /** The role the actor was acting with. `admin` marks an administrator's decision. */
  actorRole?: Role;
  onBehalfOfPersonId?: UUID;
  entityType: 'person' | 'relationship' | 'story' | 'contribution' | 'event' | 'media' | 'account' | 'grant' | 'access' | 'change_request' | 'family' | 'feedback_report' | 'recipe';
  entityId: UUID;
  action: AuditAction;
  /** Field-level before/after values (only the changed fields). */
  before?: Record<string, unknown>;
  after?: Record<string, unknown>;
  description: string;
  /** What the administrator chose to say about it. Always optional, never invented. */
  reason?: string;
  /** The picture this decision was about, so the log can show it. */
  media?: AuditMedia;
  entityVersion?: number;
  timestamp: ISODateTime;
  sessionRef?: string;
  approvalStatus?: 'n/a' | 'approved' | 'pending';
}

export interface Notification {
  id: UUID;
  accountId: UUID;
  kind: 'review' | 'approval' | 'conflict' | 'upload' | 'processing' | 'invitation' | 'story' | 'moment' | 'event' | 'visit' | 'question' | 'report' | 'recipe';
  title: string;
  titleHe: string;
  body?: string;
  bodyHe?: string;
  link?: string;
  /** Who did the thing — lets the reader's stars make it prominent. */
  actorAccountId?: UUID;
  read: boolean;
  createdAt: ISODateTime;
}

// ---------------------------------------------------------------------------
// Local-only records (prototype and offline support; production keeps these
// client-side as well — they never leave the device except as commands)
// ---------------------------------------------------------------------------

export interface RecordingDraft {
  id: UUID; // stable local draft id, doubles as idempotency root
  accountId: UUID;
  /** null while recording a brand-new story; set when contributing to one. */
  storyId: UUID | null;
  speakerPersonId: UUID;
  title: string;
  titlePending: boolean;
  date: FuzzyDate;
  peopleIds: UUID[];
  locationId?: UUID;
  /** Total recorded milliseconds across chunks. */
  durationMs: number;
  mimeType: string;
  chunkCount: number;
  /** Set once recording is finished and the blob assembled. */
  mediaAssetId?: UUID;
  textFallback?: string;
  /** Set when this recording answers a question someone asked (see StoryQuestion). */
  questionId?: UUID;
  step: number;
  createdAt: ISODateTime;
  updatedAt: ISODateTime;
}

export interface PendingOperation {
  id: UUID;
  accountId: UUID;
  command: string;
  payload: Record<string, unknown>;
  idempotencyKey: string;
  entityType?: string;
  entityId?: string;
  createdAt: ISODateTime;
  attempts: number;
  lastError?: string;
}

// ---------------------------------------------------------------------------
// Command results
// ---------------------------------------------------------------------------

export interface FieldConflict {
  field: string;
  currentValue: unknown;
  yourValue: unknown;
  baseValue: unknown;
  changedByAccountId: UUID | null;
  changedAt: ISODateTime;
}

export type CommandResult<T> =
  | { ok: true; value: T; merged?: boolean; queuedOffline?: boolean; noop?: boolean }
  | { ok: false; kind: 'conflict'; conflicts: FieldConflict[]; current: T; currentVersion: number }
  | { ok: false; kind: 'validation'; messageKey: string; params?: Record<string, string | number> }
  | { ok: false; kind: 'permission'; messageKey: string }
  | { ok: false; kind: 'review_required'; changeRequestId: UUID; messageKey: string }
  | { ok: false; kind: 'network'; messageKey: string; retryable: boolean }
  | { ok: false; kind: 'not_found'; messageKey: string }
  | { ok: false; kind: 'locked'; messageKey: string; reason?: string };

export interface Session {
  accountId: UUID;
  familyId: UUID;
  personId: UUID | null;
  /**
   * What this session may do *right now*. An administrator who has not turned
   * Admin mode on is an ordinary member here — see `adminAvailable`.
   */
  role: Role;
  /**
   * True when the account really is an administrator of this family, whether or
   * not Admin mode is on. Derived on every read; never stored. Only the burger
   * menu uses it — to offer the switch. Nothing else should grant power from it.
   */
  adminAvailable?: boolean;
  kind: AccountKind;
  temporarySessionId?: UUID;
  /** Temporary trusted-device sessions can be PIN-locked on this device. */
  pinLocked: boolean;
  onboardingComplete: boolean;
  createdAt: ISODateTime;
}

// ---------------------------------------------------------------------------
// What the family sends back: faults, ideas and questions
// ---------------------------------------------------------------------------

/**
 * Something a person told the assistant that an administrator should read.
 *
 * The point of collecting these through the assistant rather than through a
 * "contact us" form is that by the time somebody is complaining, the assistant
 * already knows what they were looking at — which screen, which mode, whether
 * they were offline, what the app last told them had gone wrong. All of that is
 * attached without a single question being asked, so what reaches the
 * administrators is a report they can act on rather than "it doesn't work".
 *
 * Nothing here is ever deleted, in keeping with the rest of the app: a report is
 * answered and closed, and the whole thread stays readable.
 */
export type ReportKind = 'fault' | 'idea' | 'question';
export type ReportStatus = 'new' | 'seen' | 'planned' | 'done' | 'declined';

/** Where they were and what was true when they wrote it. Gathered, never asked for. */
export interface ReportContext {
  /** The address they were on. */
  path: string;
  /** Screen id from the assistant's app map, when it knows the path. */
  screen?: string;
  /** Panels and cards open on top at the time. */
  open?: string[];
  lang: Lang;
  /** 'simple' | 'advanced'. */
  mode: string;
  role: Role;
  adminMode: boolean;
  online: boolean;
  /** Edits still waiting to be sent. */
  pending: number;
  /** The last error the app put on screen, if any. */
  lastError?: string;
  /** Browser and screen size — the two things that explain most layout faults. */
  userAgent?: string;
  viewport?: string;
  /** Which assistant was answering when this was sent. */
  assistant?: string;
  appVersion?: string;
}

export interface FeedbackReport {
  id: UUID;
  familyId: UUID;
  kind: ReportKind;
  /** One line, in the person's own words wherever possible. */
  title: string;
  detail?: string;
  status: ReportStatus;
  /** Who sent it. */
  accountId: UUID;
  /** Their circle, when their account is joined to one. */
  personId?: UUID;
  context: ReportContext;
  /** The exchange with the assistant that led to this, newest last. Trimmed to the last few turns. */
  conversation?: Array<{ role: 'user' | 'assistant'; text: string }>;
  /** An administrator's reply, kept with the report rather than in a separate list. */
  answer?: string;
  answeredBy?: UUID;
  answeredAt?: ISODateTime;
  createdAt: ISODateTime;
  updatedAt: ISODateTime;
}
